import axios, { AxiosInstance } from "axios";
import logger from "../utils/logger";
import { Constants } from "../utils/constants";

/**
 * Policy Management Service
 * Handles Cancellation Policies and Terms & Conditions management
 * Base URL: Uses Constants.SPACE_MANAGEMENT_API_BASE_URL
 */

export interface CancellationPolicy {
  id?: number;
  name: string;
  description: string;
  hoursBefore: number;
  refundPercentage: number;
}

export interface TermsAndConditions {
  id?: number;
  title: string;
  content: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateCancellationPolicyRequest {
  name: string;
  description: string;
  hoursBefore: number;
  refundPercentage: number;
}

export interface UpdateCancellationPolicyRequest {
  name?: string;
  description?: string;
  hoursBefore?: number;
  refundPercentage?: number;
}

export interface CreateTermsRequest {
  title: string;
  content: string;
}

export interface UpdateTermsRequest {
  title?: string;
  content?: string;
  active?: boolean;
}

export interface PageableResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

class PolicyManagementService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: `${Constants.SPACE_MANAGEMENT_API_BASE_URL}/`,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add auth token interceptor with fallback like spaceManagementService
    this.api.interceptors.request.use(config => {
      let token = localStorage.getItem("authToken");
      if (!token) {
        token = localStorage.getItem("access_token");
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      response => response,
      error => {
        logger.error("Policy Management API Error:", error);
        return Promise.reject(error);
      }
    );
  }

  // ==================== Cancellation Policies ====================

  /**
   * Get all cancellation policies
   * GET /api/v2/policies/cancellation
   */
  async getCancellationPolicies(
    pageNumber: number = 0,
    pageSize: number = 20,
    sort?: string
  ): Promise<PageableResponse<CancellationPolicy>> {
    try {
      const response = await this.api.get("/api/v2/policies/cancellation", {
        params: {
          pageNumber,
          pageSize,
          paged: true,
          ...(sort && { sort }),
        },
      });
      return response.data;
    } catch (error) {
      logger.error("Error fetching cancellation policies:", error);
      throw error;
    }
  }

  /**
   * Get a single cancellation policy by ID
   * GET /api/v2/policies/cancellation/{id}
   */
  async getCancellationPolicy(id: number): Promise<CancellationPolicy> {
    try {
      const response = await this.api.get(`/api/v2/policies/cancellation/${id}`);
      return response.data;
    } catch (error) {
      logger.error("Error fetching cancellation policy:", error);
      throw error;
    }
  }

  /**
   * Create a new cancellation policy
   * POST /api/v2/policies/cancellation
   */
  async createCancellationPolicy(data: CreateCancellationPolicyRequest): Promise<CancellationPolicy> {
    try {
      const response = await this.api.post("/api/v2/policies/cancellation", data);
      return response.data;
    } catch (error) {
      logger.error("Error creating cancellation policy:", error);
      throw error;
    }
  }

  /**
   * Update a cancellation policy
   * PATCH /api/v2/policies/cancellation/{id}
   */
  async updateCancellationPolicy(id: number, data: UpdateCancellationPolicyRequest): Promise<CancellationPolicy> {
    try {
      const response = await this.api.patch(`/api/v2/policies/cancellation/${id}`, data);
      return response.data;
    } catch (error) {
      logger.error("Error updating cancellation policy:", error);
      throw error;
    }
  }

  /**
   * Delete a cancellation policy
   * DELETE /api/v2/policies/cancellation/{id}
   */
  async deleteCancellationPolicy(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v2/policies/cancellation/${id}`);
    } catch (error) {
      logger.error("Error deleting cancellation policy:", error);
      throw error;
    }
  }

  /**
   * Get active cancellation policies
   * GET /api/v1/cancellation-policies/active
   */
  async getActiveCancellationPolicies(): Promise<CancellationPolicy[]> {
    try {
      const response = await this.api.get("/api/v1/cancellation-policies/active");
      return response.data;
    } catch (error) {
      logger.error("Error fetching active cancellation policies:", error);
      throw error;
    }
  }

  // ==================== Terms & Conditions ====================

  /**
   * Get all terms and conditions
   * GET /api/v2/terms
   */
  async getTermsAndConditions(
    pageNumber: number = 0,
    pageSize: number = 20,
    sort?: string
  ): Promise<PageableResponse<TermsAndConditions>> {
    try {
      const response = await this.api.get("/api/v2/terms", {
        params: {
          pageNumber,
          pageSize,
          paged: true,
          ...(sort && { sort }),
        },
      });
      return response.data;
    } catch (error) {
      logger.error("Error fetching terms and conditions:", error);
      throw error;
    }
  }

  /**
   * Get a single terms and conditions by ID
   * GET /api/v2/terms/{id}
   */
  async getTermsById(id: number): Promise<TermsAndConditions> {
    try {
      const response = await this.api.get(`/api/v2/terms/${id}`);
      return response.data;
    } catch (error) {
      logger.error("Error fetching terms and conditions:", error);
      throw error;
    }
  }

  /**
   * Create new terms and conditions
   * POST /api/v2/terms
   */
  async createTerms(data: CreateTermsRequest): Promise<TermsAndConditions> {
    try {
      const response = await this.api.post("/api/v2/terms", data);
      return response.data;
    } catch (error) {
      logger.error("Error creating terms and conditions:", error);
      throw error;
    }
  }

  /**
   * Update terms and conditions
   * PATCH /api/v2/terms/{id}
   */
  async updateTerms(id: number, data: UpdateTermsRequest): Promise<TermsAndConditions> {
    try {
      const response = await this.api.patch(`/api/v2/terms/${id}`, data);
      return response.data;
    } catch (error) {
      logger.error("Error updating terms and conditions:", error);
      throw error;
    }
  }

  /**
   * Delete terms and conditions
   * DELETE /api/v2/terms/{id}
   */
  async deleteTerms(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v2/terms/${id}`);
    } catch (error) {
      logger.error("Error deleting terms and conditions:", error);
      throw error;
    }
  }
}

// Export a singleton instance
const policyManagementService = new PolicyManagementService();
export default policyManagementService;
